import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  alpha,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../lib/axios';
import { CustomerType, Customer } from '../../../types/customer';
import { SaleMode } from './B2BModeToggle';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface CustomerSelectorProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  onCreateClick: () => void;
  colors: POSColors;
  saleMode?: SaleMode;
}

// Special option for creating a new customer
const CREATE_CUSTOMER_OPTION = {
  id: '__create__',
  firstName: 'Create',
  lastName: 'Customer',
  isCreateOption: true,
} as Customer & { isCreateOption: boolean };

// Helper function to get customer display name
const getDisplayName = (customer: Customer): string => {
  if (customer.type === CustomerType.BUSINESS && customer.companyName) {
    return customer.companyName;
  }
  return `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
};

// Helper to check if customer is a business customer
const isBusinessCustomer = (customer: Customer): boolean => {
  return customer.type === CustomerType.BUSINESS;
};

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  selectedCustomer,
  onCustomerSelect,
  onCreateClick,
  colors,
  saleMode = 'b2c',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Determine customer type filter based on sale mode
  // B2B mode: show only business customers
  // B2C mode: show all customers (no type filter)
  const customerTypeFilter = saleMode === 'b2b' ? CustomerType.BUSINESS : undefined;

  // Fetch customers based on search term and sale mode
  const { data: customersData, isLoading } = useQuery({
    queryKey: ['pos-customers', searchTerm, saleMode],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 1) {
        return [];
      }
      const params: Record<string, string | number> = {
        search: searchTerm,
        limit: 10,
      };
      // Add type filter for B2B mode (only business customers)
      if (customerTypeFilter) {
        params.type = customerTypeFilter;
      }
      const response = await axiosInstance.get('/customers', {
        params,
      });
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
    enabled: searchTerm.length >= 1,
    staleTime: 30000, // Cache for 30 seconds
  });

  const customers = customersData || [];

  // Build options: customers + create option
  const options = [
    ...customers,
    ...(searchTerm.length > 0 ? [CREATE_CUSTOMER_OPTION] : []),
  ];

  // Debounce timeout ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Debounce the search
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = (
    _event: React.SyntheticEvent,
    value: (Customer & { isCreateOption?: boolean }) | null
  ) => {
    if (value && 'isCreateOption' in value && value.isCreateOption) {
      onCreateClick();
      setInputValue('');
      setSearchTerm('');
    } else {
      onCustomerSelect(value);
      if (value) {
        setInputValue(getDisplayName(value));
      }
    }
  };

  const handleClear = () => {
    onCustomerSelect(null);
    setInputValue('');
    setSearchTerm('');
  };

  // If customer is selected, show selected state
  if (selectedCustomer) {
    const isBusiness = isBusinessCustomer(selectedCustomer);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: alpha(colors.accent, 0.1),
          borderRadius: '10px',
          border: `1px solid ${alpha(colors.accent, 0.3)}`,
        }}
      >
        {isBusiness ? (
          <BusinessIcon sx={{ color: colors.accent, fontSize: 20 }} />
        ) : (
          <PersonIcon sx={{ color: colors.accent, fontSize: 20 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {getDisplayName(selectedCustomer)}
            </Typography>
            {isBusiness && (
              <Chip
                label="Business"
                size="small"
                sx={{
                  fontSize: '9px',
                  height: '16px',
                  backgroundColor: alpha(colors.accent, 0.2),
                  color: colors.accent,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          {selectedCustomer.phone && (
            <Typography
              sx={{
                fontSize: '11px',
                color: colors.textMuted,
              }}
            >
              {selectedCustomer.phone}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={handleClear}
          aria-label="clear"
          sx={{
            color: colors.textMuted,
            '&:hover': {
              color: colors.textPrimary,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  // Default state: show search autocomplete
  return (
    <Box>
      {/* Walk-in Customer Label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <PersonIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: colors.textMuted,
          }}
        >
          Walk-in Customer
        </Typography>
      </Box>

      {/* Customer Search Autocomplete */}
      <Autocomplete
        options={options}
        getOptionLabel={(option) => {
          if ('isCreateOption' in option && option.isCreateOption) {
            return 'Create Customer';
          }
          return getDisplayName(option);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={isLoading}
        noOptionsText={
          searchTerm.length > 0
            ? (saleMode === 'b2b' ? 'No business customers found' : 'No customers found')
            : (saleMode === 'b2b' ? 'Type to search business customers' : 'Type to search customers')
        }
        filterOptions={(x) => x} // Disable client-side filtering, we filter on server
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={saleMode === 'b2b' ? 'Search business customer...' : 'Search customer...'}
            size="small"
            label="Customer"
            InputLabelProps={{ shrink: true, sx: { display: 'none' } }}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Search customer',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surface,
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.textMuted,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.accent,
                  borderWidth: 2,
                },
              },
              '& input': {
                color: colors.textPrimary,
                '&::placeholder': {
                  color: colors.textMuted,
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          // Create Customer option
          if ('isCreateOption' in option && option.isCreateOption) {
            return (
              <li {...props} key="create-customer">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    color: colors.accent,
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    Create Customer
                  </Typography>
                </Box>
              </li>
            );
          }

          // Regular customer option
          const isBusiness = isBusinessCustomer(option);
          return (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, width: '100%' }}>
                {isBusiness ? (
                  <BusinessIcon sx={{ fontSize: 18, color: colors.accent, mt: 0.25 }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 18, color: colors.textMuted, mt: 0.25 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      {getDisplayName(option)}
                    </Typography>
                    {isBusiness && (
                      <Chip
                        label="Business"
                        size="small"
                        sx={{
                          fontSize: '9px',
                          height: '16px',
                          backgroundColor: alpha(colors.accent, 0.15),
                          color: colors.accent,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  {option.phone && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: colors.textMuted,
                      }}
                    >
                      {option.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          );
        }}
        ListboxProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            mt: 0.5,
            '& .MuiAutocomplete-option': {
              '&:hover': {
                backgroundColor: alpha(colors.accent, 0.1),
              },
              '&[aria-selected="true"]': {
                backgroundColor: alpha(colors.accent, 0.15),
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default CustomerSelector;
